import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  ChevronRight,
  ShieldCheck,
  Clock,
  BadgeCheck,
  Zap,
  Play,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { services, stats, values, googleReviews, google, industries, partners } from "../mock";
import GoogleReviewCard from "../components/GoogleReviewCard";
import ServiceIcon from "../components/ServiceIcon";
import CountUp from "../components/CountUp";
import GoogleReviewBadge from "../components/GoogleReviewBadge";
import useReveal from "../hooks/useReveal";

export default function Home() {
  useReveal();

  return (
    <div className="-mt-20">
      {/* CINEMATIC DARK HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-slate-950 text-white pt-24">
        {/* animated grid */}
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-70" />
        {/* gradient orbs */}
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-sky-500/25 blur-[120px] animate-float-slow" />
        <div className="absolute -bottom-40 -right-32 h-[520px] w-[520px] rounded-full bg-cyan-400/20 blur-[120px] animate-float-slower" />
        {/* subtle vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/0 via-transparent to-slate-950" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12 items-center w-full">
          <div className="lg:col-span-7">
            <div className="reveal inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur text-xs font-semibold tracking-[0.2em] text-sky-300">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75 pulse-ring" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-400" />
              </span>
              IT · AV · SECURITY — SINCE 2010
            </div>

            <h1 className="reveal font-display mt-7 text-[44px] sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight">
              Turnkey technology,
              <br />
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-sky-300 via-cyan-300 to-white bg-clip-text text-transparent">
                  engineered to endure.
                </span>
                <svg className="absolute -bottom-2 left-0 w-full" height="14" viewBox="0 0 400 14" fill="none" preserveAspectRatio="none">
                  <path d="M2 10 Q100 2, 200 8 T398 6" stroke="url(#g1)" strokeWidth="3" strokeLinecap="round" fill="none" />
                  <defs>
                    <linearGradient id="g1" x1="0" x2="1">
                      <stop offset="0" stopColor="#38bdf8" />
                      <stop offset="1" stopColor="#22d3ee" />
                    </linearGradient>
                  </defs>
                </svg>
              </span>
            </h1>

            <p className="reveal mt-7 text-lg text-slate-300 max-w-xl leading-relaxed">
              From resilient networks and boardroom AV to fire safety, access
              control and video surveillance — delivered end-to-end by a single
              team that owns design, deployment and lifetime support.
            </p>

            <div className="reveal mt-9 flex flex-wrap items-center gap-4">
              <Link to="/contact">
                <Button className="group bg-sky-500 hover:bg-sky-400 text-white h-12 px-7 rounded-md shadow-[0_10px_40px_-10px_rgba(56,189,248,0.6)]">
                  Start a Project
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/services" className="group inline-flex items-center gap-3 text-white/90 hover:text-white">
                <span className="h-11 w-11 rounded-full border border-white/20 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                  <Play className="h-4 w-4 fill-white" />
                </span>
                <span className="text-sm font-medium">Explore Services</span>
              </Link>
            </div>

            <div className="reveal mt-12 flex flex-wrap gap-x-8 gap-y-4">
              {[
                { icon: ShieldCheck, label: "Certified Engineers" },
                { icon: Clock, label: "Rapid AMC Response" },
                { icon: BadgeCheck, label: "OEM Partnerships" },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-2 text-slate-300">
                  <b.icon className="h-4 w-4 text-sky-400" />
                  <span className="text-sm font-medium">{b.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Orbiting service showcase */}
          <div className="reveal lg:col-span-5 relative h-[520px] hidden lg:block">
            <div className="absolute inset-0 flex items-center justify-center">
              {/* orbit rings */}
              <div className="absolute h-[380px] w-[380px] rounded-full border border-sky-500/20" />
              <div className="absolute h-[280px] w-[280px] rounded-full border border-sky-500/25" />
              <div className="absolute h-[180px] w-[180px] rounded-full border border-sky-500/30" />

              {/* Center core */}
              <div className="relative h-32 w-32 rounded-2xl bg-gradient-to-br from-sky-400 to-cyan-600 shadow-2xl shadow-sky-500/50 flex items-center justify-center text-white">
                <span className="absolute inset-0 rounded-2xl ring-1 ring-white/30" />
                <Zap className="h-14 w-14" strokeWidth={1.5} />
                <span className="absolute -bottom-8 text-[10px] uppercase tracking-[0.25em] text-sky-200">
                  Global Tech
                </span>
              </div>

              {/* Orbiting icons */}
              {[
                { icon: "Network", top: "6%", left: "46%" },
                { icon: "Camera", top: "20%", left: "84%" },
                { icon: "Fingerprint", top: "64%", left: "90%" },
                { icon: "Flame", top: "88%", left: "46%" },
                { icon: "Presentation", top: "64%", left: "2%" },
                { icon: "Shield", top: "20%", left: "6%" },
              ].map((o) => (
                <div
                  key={`${o.icon}-${o.top}-${o.left}`}
                  className="absolute h-14 w-14 -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white/5 backdrop-blur border border-white/10 flex items-center justify-center text-sky-300 hover:text-white hover:bg-white/10 hover:scale-110 transition-all"
                  style={{ top: o.top, left: o.left }}
                >
                  <ServiceIcon name={o.icon} className="h-6 w-6" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Partners marquee */}
        <div className="absolute bottom-0 inset-x-0 py-6 border-t border-white/10 bg-slate-950/60 backdrop-blur">
          <div className="flex items-center gap-3 max-w-7xl mx-auto px-6">
            <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500 whitespace-nowrap">
              Trusted OEMs
            </span>
            <div className="relative flex-1 overflow-hidden">
              <div className="flex gap-12 animate-marquee whitespace-nowrap">
                {[...partners, ...partners].map((p, i) => (
                  <span key={`${p}-${i}`} className="font-display text-slate-500 hover:text-sky-300 transition-colors text-sm font-semibold tracking-wider">
                    {p}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS - animated counters */}
      <section className="relative bg-white py-20 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => {
            const num = parseInt(s.value, 10);
            const suffix = s.value.replace(/[0-9]/g, "");
            return (
              <div key={s.label} className="reveal text-center">
                <div className="font-display text-5xl lg:text-6xl font-bold bg-gradient-to-b from-slate-900 to-slate-600 bg-clip-text text-transparent">
                  {isNaN(num) || /\D.*\d/.test(s.value) ? s.value : <><CountUp end={num} />{suffix}</>}
                </div>
                <div className="mt-2 text-sm text-slate-500 uppercase tracking-widest">
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-28 bg-slate-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 h-72 w-72 rounded-full bg-sky-100/60 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
            <div className="reveal max-w-2xl">
              <div className="inline-flex items-center gap-2 text-sky-700 font-semibold text-xs tracking-[0.25em] uppercase">
                <Sparkles className="h-3.5 w-3.5" /> What we do
              </div>
              <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
                One partner. Every layer of your infrastructure.
              </h2>
              <p className="mt-5 text-slate-600 text-lg">
                Consolidated engineering across networking, AV and life-safety
                systems — designed together, delivered together.
              </p>
            </div>
            <Link
              to="/services"
              className="group inline-flex items-center gap-2 text-sky-700 font-medium hover:text-sky-800"
            >
              View all services
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.slice(0, 6).map((s, i) => (
              <Link
                to="/services"
                key={s.slug}
                className="reveal card-shine group relative bg-white rounded-2xl p-7 border border-slate-200 hover:border-sky-400 hover:-translate-y-1 hover:shadow-xl hover:shadow-sky-500/10 transition-all duration-500"
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                <div className="relative z-10">
                  <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-sky-50 to-cyan-50 text-sky-700 flex items-center justify-center group-hover:from-sky-500 group-hover:to-cyan-500 group-hover:text-white transition-all duration-500 shadow-sm">
                    <ServiceIcon name={s.icon} className="h-7 w-7" />
                  </div>
                  <h3 className="font-display mt-6 text-xl font-semibold text-slate-900">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {s.short}
                  </p>
                  <div className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-sky-700">
                    Learn more
                    <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US - split layout with image collage */}
      <section className="py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div className="reveal">
            <div className="text-sky-700 font-semibold text-xs tracking-[0.25em] uppercase">
              Why Global Tech
            </div>
            <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
              Deep expertise.
              <br />
              <span className="text-sky-600">Honest engineering.</span>
            </h2>
            <p className="mt-5 text-slate-600 text-lg max-w-lg">
              We combine technical depth across disciplines with a
              customer-first delivery model — so your infrastructure just
              works, day after day.
            </p>
            <div className="mt-10 space-y-4">
              {values.map((v, i) => (
                <div key={v.title} className="reveal group flex gap-5 p-5 rounded-xl hover:bg-slate-50 transition-colors" style={{ transitionDelay: `${i * 60}ms` }}>
                  <div className="h-12 w-12 shrink-0 rounded-lg bg-sky-600 text-white flex items-center justify-center shadow-md shadow-sky-500/30 group-hover:scale-110 transition-transform">
                    <BadgeCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-slate-900 text-lg">
                      {v.title}
                    </div>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                      {v.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="reveal grid grid-cols-2 gap-4">
            <div className="relative rounded-2xl overflow-hidden group h-72">
              <img src="https://img1.wsimg.com/isteam/ip/0ed9d86f-1518-4c8b-9bd3-8703aae28eb3/ols/IT.jpg/:/rs=w:600,cg:true" alt="IT & Networking" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white text-sm font-semibold">IT & Networking</div>
            </div>
            <div className="relative rounded-2xl overflow-hidden group h-72 mt-10">
              <img src="https://img1.wsimg.com/isteam/ip/0ed9d86f-1518-4c8b-9bd3-8703aae28eb3/ols/CCTV.jpg/:/rs=w:600,cg:true" alt="Video Surveillance" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white text-sm font-semibold">Video Surveillance</div>
            </div>
            <div className="relative rounded-2xl overflow-hidden group h-72">
              <img src="https://img1.wsimg.com/isteam/ip/0ed9d86f-1518-4c8b-9bd3-8703aae28eb3/blob-3b24997.png/:/rs=w:600,cg:true" alt="Boardroom AV" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white text-sm font-semibold">Boardroom AV</div>
            </div>
            <div className="relative rounded-2xl overflow-hidden group h-72 mt-10">
              <img src="https://img1.wsimg.com/isteam/ip/0ed9d86f-1518-4c8b-9bd3-8703aae28eb3/ols/ACS.jpg/:/rs=w:600,cg:true" alt="Access Control" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white text-sm font-semibold">Access Control</div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES - dark section with grid */}
      <section className="relative py-28 bg-slate-950 text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />
        <div className="absolute -top-32 left-1/4 h-80 w-80 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="reveal max-w-2xl">
            <div className="text-sky-400 font-semibold text-xs tracking-[0.25em] uppercase">
              Industries served
            </div>
            <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight">
              Trusted where uptime matters most.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3">
            {industries.map((i, idx) => (
              <div
                key={i}
                className="reveal group relative px-5 py-6 rounded-xl bg-white/5 border border-white/10 hover:border-sky-400/60 hover:bg-white/[0.08] transition-all overflow-hidden"
                style={{ transitionDelay: `${idx * 40}ms` }}
              >
                <div className="absolute -bottom-8 -right-8 h-24 w-24 rounded-full bg-sky-500/20 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative text-sm font-semibold">{i}</div>
                <div className="relative text-xs text-slate-400 mt-1">Enterprise grade</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="reveal text-center max-w-2xl mx-auto">
            <div className="text-sky-700 font-semibold text-xs tracking-[0.25em] uppercase">
              Real Google Reviews
            </div>
            <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              Rated {google.rating.toFixed(1)} by our clients on Google
            </h2>
          </div>
          <div className="mt-16 grid gap-6 md:grid-cols-3" data-testid="google-reviews-grid">
            {googleReviews.map((r, i) => (
              <GoogleReviewCard key={r.name} review={r} index={i} />
            ))}
          </div>

          <div className="reveal mt-10 max-w-2xl mx-auto">
            <GoogleReviewBadge />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="reveal relative overflow-hidden rounded-3xl bg-slate-950 p-12 lg:p-20 text-white">
            <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />
            <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-sky-500/30 blur-3xl animate-float-slow" />
            <div className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-cyan-400/20 blur-3xl animate-float-slower" />
            <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div className="max-w-2xl">
                <div className="text-sky-300 text-xs uppercase tracking-[0.25em] font-semibold">
                  Ready to start?
                </div>
                <h3 className="font-display mt-3 text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                  Let's build your next project together.
                </h3>
                <p className="mt-4 text-slate-300">
                  Tell us about your requirement and we'll come back with a
                  tailored proposal and timeline.
                </p>
              </div>
              <Link to="/contact">
                <Button className="bg-sky-500 hover:bg-sky-400 text-white h-12 px-7 rounded-md font-semibold shadow-[0_10px_40px_-10px_rgba(56,189,248,0.7)]">
                  Contact Sales
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
